/**
 * useCep Hook
 * 
 * Hook for fetching address data from CEP with loading and error states.
 */
import { useState, useCallback } from 'react';
import { fetchAddressByCep, isValidCep, cleanCep, type AddressData } from '@/services/cep.service';
import { useToast } from '@/hooks/use-toast';

interface UseCepResult {
  data: AddressData | null;
  isLoading: boolean;
  error: string | null;
  fetchAddress: (cep: string) => Promise<AddressData | null>;
  clearData: () => void;
}

export function useCep(): UseCepResult {
  const [data, setData] = useState<AddressData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchAddress = useCallback(async (cep: string): Promise<AddressData | null> => {
    const cleaned = cleanCep(cep);
    
    // Only fetch if CEP is complete
    if (cleaned.length !== 8) {
      return null;
    }
    
    if (!isValidCep(cep)) {
      setError('CEP inválido');
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const addressData = await fetchAddressByCep(cep);
      
      if (!addressData) {
        setError('CEP não encontrado');
        toast({
          title: 'CEP não encontrado',
          description: 'Verifique o CEP informado e tente novamente.',
          variant: 'destructive',
        });
        return null;
      }
      
      setData(addressData);
      return addressData;
    } catch (err) {
      console.warn('Falha ao buscar CEP:', err);
      const message = 'Erro ao buscar CEP';
      setError(message);
      toast({
        title: message,
        description: 'Tente novamente em alguns instantes.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const clearData = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return {
    data,
    isLoading,
    error,
    fetchAddress,
    clearData,
  };
}

export { formatCep } from '@/services/cep.service';
export { cleanCep, isValidCep };
export type { AddressData };

